import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, Signal, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DragScrollDirective } from '@app/_directives/drag-scroll.directive';
import { CacheService } from '@app/_services/cache.service';
import { FeatureStore, HabitStore, JournalStore, ListStore, MoneyTrackerStore, NoteStore, TodoStore } from '@app/_stores';
import { AttachmentType, CollaboraterRole, EntityName, EntityStatus } from '@app/_types/generic.type';
import { FilterAttachmentComponent } from '@app/components/shared/filters/filter-attachment/filter-attachment.component';
import { FilterHashtagComponent } from '@app/components/shared/filters/filter-hashtag/filter-hashtag.component';
import { FilterMoodComponent } from '@app/components/shared/filters/filter-mood/filter-mood.component';
import { FilterSearchComponent } from '@app/components/shared/filters/filter-search/filter-search.component';
import { FilterDateRangeComponent } from '@app/components/shared/filters/filter-date-range/filter-date-range.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { AttachmentListComponent } from '../attachment-list/attachment-list.component';
import { Attachment, AttachmentEntity } from '@app/_interfaces/generic.interface';
import { log } from '@app/_services/logger';
// NOTE: Form components are loaded dynamically to break circular dependencies
import { FilterStatusComponent } from '@app/components/shared/filters/filter-status/filter-status.component';
import { SettingsService } from '@app/_services/settings.service';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { NotificationComponent } from '@app/components/common/notifications/notification/notification.component';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsService } from '@app/_services/analytics.service';
import { StorageService } from '@app/_services/storage.servive';
import { AnalyticsScreen } from '@app/_types/analytics-events.types';
import { AlertService } from '@app/_services/alert.service';
import { FilterRoleComponent } from '@app/components/shared/filters/filter-role/filter-role.component';
import { FilterFeatureComponent } from '@app/components/shared/filters/filter-feature/filter-feature.component';
import { AttachmentsService } from '@app/_services/attachments.service';
import { ModalService } from '@app/_services/modal.service';
import { toDate } from '@app/_services/date';
import { GalleryViewComponent } from '../gallery-view/gallery-view.component';

export type GalleryGroupByMode = 'entityTitle' | 'month' | 'none';

/** A single flattened attachment item with parent entity context */
export interface FlatAttachmentItem {
  attachment: Attachment;
  entity: AttachmentEntity;
  createdAt: Date;
}

/** A month group for the "group by month" view */
export interface MonthGroup {
  label: string;
  items: FlatAttachmentItem[];
}

@Component({
  selector: 'app-attachment',
  imports: [
    CommonModule,
    SvgComponent,
    FilterSearchComponent,
    FilterHashtagComponent,
    FilterAttachmentComponent,
    FilterStatusComponent,
    FilterMoodComponent,
    FilterRoleComponent,
    FilterFeatureComponent,
    FilterDateRangeComponent,
    DragScrollDirective,
    AttachmentListComponent,
    NoResultBlockComponent,
    NotificationComponent,
  ],
  templateUrl: './attachment.component.html',
  styleUrl: './attachment.component.scss'
})

export class AttachmentComponent implements OnInit, OnDestroy {

  readonly featureStore = inject(FeatureStore);
  readonly todoStore = inject(TodoStore);
  readonly noteStore = inject(NoteStore);
  readonly listStore = inject(ListStore);
  readonly habitStore = inject(HabitStore);
  readonly journalStore = inject(JournalStore);
  readonly moneyTrackerStore = inject(MoneyTrackerStore);

  isFilter: WritableSignal<boolean> = signal(false);

  /** Restrict date filter to past dates only (yesterday and earlier) */
  readonly maxDate: Date = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(23, 59, 59, 999);
    return d;
  })();

  /** Group by mode — persisted to viewSettings */
  groupBy: WritableSignal<GalleryGroupByMode> = signal<GalleryGroupByMode>('entityTitle');


  filters: {
    query: WritableSignal<string>;
    tags: WritableSignal<string[]>;
    attachments: WritableSignal<AttachmentType[]>;
    moods: WritableSignal<number[]>;
    status: WritableSignal<EntityStatus[]>;
    role: WritableSignal<CollaboraterRole[]>;
    feature: WritableSignal<EntityName[]>;
    dateFrom: WritableSignal<Date | null>;
    dateTo: WritableSignal<Date | null>;
  } = {
      query: signal<string>(''),
      tags: signal<string[]>([]),
      attachments: signal<AttachmentType[]>([]),
      moods: signal<number[]>([]),
      status: signal<EntityStatus[]>([]),
      role: signal<CollaboraterRole[]>([]),
      feature: signal<EntityName[]>([]),
      dateFrom: signal<Date | null>(null),
      dateTo: signal<Date | null>(null),
    };

  hasAnyAttachments: Signal<boolean> = computed(() => {
    const loggedIn = this.cc.loggedIn();
    if (!loggedIn) return false;
    return this.attachmentsService.entitiesWithAttachments().length > 0;
  });

  entitiesWithAttachments: Signal<AttachmentEntity[]> = computed(() => {
    const loggedIn = this.cc.loggedIn();
    const user = this.cc.user;
    if (!loggedIn) return [];
    if (!user) return [];
    const allAttachments = this.attachmentsService.entitiesWithAttachments();
    return this.filterAttachments(allAttachments);
  });

  /** All attachment items flattened from filtered entities — used for month/none views */
  flatAttachmentItems: Signal<FlatAttachmentItem[]> = computed(() => {
    const entities = this.entitiesWithAttachments();
    const items: FlatAttachmentItem[] = [];
    for (const entity of entities) {
      for (const attachment of entity.attachments) {
        const createdAt = attachment.createdAt instanceof Date
          ? attachment.createdAt
          : (toDate(attachment.createdAt) || new Date());
        items.push({ attachment, entity, createdAt });
      }
    }
    // Sort by most recent first
    items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return items;
  });

  /** Month groups for "group by month" view */
  monthGroups: Signal<MonthGroup[]> = computed(() => {
    const items = this.flatAttachmentItems();
    const grouped = new Map<string, FlatAttachmentItem[]>();
    const now = new Date();
    for (const item of items) {
      const year = item.createdAt.getFullYear();
      const month = item.createdAt.toLocaleString('en', { month: 'long' });
      const label = year === now.getFullYear() ? month : `${month} ${year}`;
      if (!grouped.has(label)) grouped.set(label, []);
      grouped.get(label)!.push(item);
    }
    return Array.from(grouped.entries()).map(([label, items]) => ({ label, items }));
  });

  /** Per-month scroll state for arrow visibility */
  monthScrollStates = signal<Record<string, { canLeft: boolean; canRight: boolean }>>({});

  onMonthRowScroll(label: string, el: HTMLElement): void {
    this.monthScrollStates.update(states => ({
      ...states,
      [label]: {
        canLeft: el.scrollLeft > 0,
        canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      },
    }));
  }

  monthScrollLeft(el: HTMLElement): void {
    el.scrollBy({ left: -220, behavior: 'smooth' });
  }

  monthScrollRight(el: HTMLElement): void {
    el.scrollBy({ left: 220, behavior: 'smooth' });
  }

  screenEnterTime: Signal<Date> = signal(new Date());
  unSubscribe = new Subject<void>();

  /** Tracks which entity rows are scrolled (for showing count badge) */
  scrolledEntities = new Set<string>();

  onEntityScrollStateChange(entityId: string, scrolled: boolean) {
    if (scrolled) {
      this.scrolledEntities.add(entityId);
    } else {
      this.scrolledEntities.delete(entityId);
    }
  }

  /** Entity name to SVG icon name mapping (matches Flutter feature icons) */
  entityIconMap: Record<EntityName, string> = {
    todo: 'todo',
    habit: 'habit',
    journal: 'journal',
    note: 'note',
    moneyTracker: 'moneyTracker',
    list: 'list',
    calendarIntegration: 'calendarIntegration',
    focus: 'focus'
  };


  isFiltered(): boolean {
    const filters = this.filters;
    return filters.query() !== '' || filters.tags().length > 0 || filters.attachments().length > 0 || filters.moods().length > 0 || filters.status().length > 0 || filters.role().length > 0 || filters.feature().length > 0 || filters.dateFrom() !== null || filters.dateTo() !== null;
  }

  constructor(
    public cc: CacheService,
    public ss: SettingsService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private attachmentsService: AttachmentsService,
    private alertService: AlertService,
    private modalService: ModalService,
    private router: Router,
  ) {

  }

  ngOnInit() {
    this.analyticsInit();
    // Load persisted groupBy from viewSettings (validate against known modes to reject stale values)
    const validGroupByModes: GalleryGroupByMode[] = ['entityTitle', 'month', 'none'];
    const viewSignals = this.ss.viewSignals();
    const savedGroupBy = viewSignals['galleryGroupBy']?.() as GalleryGroupByMode | undefined;
    if (savedGroupBy && validGroupByModes.includes(savedGroupBy)) {
      this.groupBy.set(savedGroupBy);
    }
  }

  navigateToToday() {
    this.router.navigate(['/today']);
  }

  /** Format attachment date for overlay label (matches Flutter formatAttachmentDate) */
  formatAttachmentDate(date: Date): string {
    const now = new Date();
    if (date.getFullYear() !== now.getFullYear()) {
      // "12 May 2025" format
      return date.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });
    }
    // "Mon, 12 May" format
    return date.toLocaleDateString('en', { weekday: 'short', day: 'numeric', month: 'short' });
  }

  openKebabMenu() {
    this.modalService.openLevel3(GalleryViewComponent, {
      groupBy: this.groupBy,
    }, { disableClose: true });
  }

  filterAttachments(attachmentEntities: AttachmentEntity[]): AttachmentEntity[] {
    const filters = this.filters;
    const query = filters.query().toLowerCase();
    const tags = filters.tags();
    const attachments = filters.attachments();
    const moods = filters.moods();
    const status = filters.status();
    const role = filters.role();
    const feature = filters.feature();
    const dateFrom = filters.dateFrom();
    const dateTo = filters.dateTo();
    const currentUid = this.cc.user.uid;

    const filteredAttachments = attachmentEntities.map(entity => {
      const matchesQuery = !query || entity.title.toLowerCase().includes(query);
      const matchesTags = tags.length === 0 || (entity.tags || []).some(tag => tags.includes(tag));
      const matchesAttachments = attachments.length === 0 || (entity.attachments || []).some(att => attachments.includes(att.fileType));
      const matchesMood = moods.length === 0 || (entity.emotion !== null ? moods.includes(entity.emotion) : false);
      const matchesStatus = status.length === 0 || entity.status.some(st => status.includes(st));
      const matchesFeature = feature.length === 0 || feature.includes(entity.type);
      const entityRole: CollaboraterRole = entity.memberRole || (entity.uid === currentUid ? 'owner' : 'viewer');
      const matchesRole = role.length === 0 || role.includes(entityRole);

      // Entity-level filters
      if (!matchesQuery || !matchesTags || !matchesAttachments || !matchesMood || !matchesStatus || !matchesFeature || !matchesRole) {
        return null;
      }

      // Filter individual attachments by type and date range.
      let filteredAtts = entity.attachments;

      // Attachment type filter: only show matching file types
      if (attachments.length > 0) {
        filteredAtts = filteredAtts.filter(att => attachments.includes(att.fileType));
      }

      // Date range filter: filter by createdAt
      if (dateFrom || dateTo) {
        filteredAtts = filteredAtts.filter(att => {
          const createdAt = att.createdAt instanceof Date ? att.createdAt : (toDate(att.createdAt) || new Date());
          if (dateFrom && createdAt < dateFrom) return false;
          if (dateTo) {
            const endOfDay = new Date(dateTo);
            endOfDay.setHours(23, 59, 59, 999);
            if (createdAt > endOfDay) return false;
          }
          return true;
        });
      }

      if (filteredAtts.length === 0) return null;
      if (filteredAtts.length !== entity.attachments.length) {
        return { ...entity, attachments: filteredAtts };
      }

      return entity;
    }).filter((entity): entity is AttachmentEntity => entity !== null);

    return filteredAttachments;
  }

  async toggleFilter() {
    if (!this.isFilter()) {
      this.isFilter.set(true);
      return;
    } else {
      const isFilterApplied = this.isFiltered();
      if (isFilterApplied) {
        const confirmed = await this.alertService.confirmRevamped({
          title: this.ss.t('filterApplied_title'),
          description: this.ss.t('filterApplied_content'),
          yesButtonText: this.ss.t('common_proceed'),
          noButtonText: this.ss.t('common_buttonCancel'),
          yesButtonColor: 'color-14',
          noButtonColor: 'color-7',
        });
        if (confirmed) {
          this.clearFilters();
          this.isFilter.set(false);
        } else {
          return;
        }
      } else {
        this.isFilter.set(false);
      }
    }
  }

  async openEntity(entity: EntityName, entityId: string) {
    switch (entity) {
      case 'todo':
        const todo = this.todoStore.idToTodo()[entityId];
        const { TodoFormComponent } = await import('@app/components/features/todos/todo-form/todo-form.component');
        this.modalService.openLevel1(
          TodoFormComponent,
          {
            mode: 'edit',
            value: todo,
            dateString: todo.startDate || null
          },
          { disableClose: true }
        );
        break;
      case 'note':
        const { NoteFormComponent } = await import('@app/components/features/notes/note-form/note-form.component');
        this.modalService.openLevel1(
          NoteFormComponent,
          {
            mode: 'edit',
            value: this.noteStore.idToNote()[entityId],
          },
          { disableClose: true }
        );
        break;
      case 'habit':
        const habit = this.habitStore.idToHabit()[entityId];
        const { HabitFormComponent } = await import('@app/components/features/habits/habit-form/habit-form.component');
        this.modalService.openLevel1(
          HabitFormComponent,
          {
            mode: 'edit',
            value: habit,
            setupId: habit.setupId,
            dateString: habit.dueDate
          },
          { disableClose: true, autoFocus: false }
        );
        break;
      case 'journal':
        const journal = this.journalStore.idToJournal()[entityId];
        const { JournalFormComponent } = await import('@app/components/features/journals/journal-form/journal-form.component');
        this.modalService.openLevel1(
          JournalFormComponent,
          {
            mode: 'edit',
            value: journal,
            setupId: journal.setupId,
            dateString: journal.dueDate
          },
          { disableClose: true, autoFocus: false }
        );
        break;
      case 'moneyTracker':
        const transaction = this.moneyTrackerStore.idToTransaction()[entityId];
        const { MoneyTransactionFormComponent } = await import('@app/components/features/money-tracker/money-transaction-form/money-transaction-form.component');
        this.modalService.openLevel1(
          MoneyTransactionFormComponent,
          {
            mode: 'edit',
            value: transaction,
            transactionType: transaction.transactionType
          },
          { disableClose: true }
        );
        break;
      case 'list':
        const { ListItemsComponent } = await import('@app/components/features/lists/list-items/list-items.component');

        this.modalService.openLevel1(
          ListItemsComponent,
          { mode: 'edit', value: this.listStore.idToList()[entityId] },
          { disableClose: true, autoFocus: false }
        );
        break;
      default:
        log.warn('AttachmentComponent.openEntity', `Unknown entity: ${entity}`, { entity });
        break;
    }
  };

  async deleteAttachment(attachments: Attachment[], entity: AttachmentEntity) {
    if (entity.type === 'list') {
      const list = this.listStore.myActiveLists().find(l => l.id === entity.id);
      if (!list) return;
      attachments.forEach(attach => {
        if (list.attachments[attach.id]) {
          list.attachments[attach.id].deletedAt = new Date();
        }
      });
      this.listStore.updateLists([list]);
      return;
    }

    const storeMap: any = {
      todo: { store: this.todoStore, update: 'updateTodos', get: 'idToTodo' },
      note: { store: this.noteStore, update: 'updateNotes', get: 'idToNote' },
      habit: { store: this.habitStore, update: 'updateHabits', get: 'idToHabit' },
      journal: { store: this.journalStore, update: 'updateJournals', get: 'idToJournal' },
      moneyTracker: { store: this.moneyTrackerStore, update: 'updateMoneyTransactions', get: 'idToTransaction' }
    };

    const config = storeMap[entity.type];
    if (!config) return;

    const item = config.store[config.get]()[entity.id];
    attachments.forEach(attach => {
      if (item.attachments[attach.id]) {
        item.attachments[attach.id].deletedAt = new Date();
      }
    });
    config.store[config.update]([item]);
  }

  clearFilters() {
    const filters = this.filters;
    filters.query.set('');
    filters.tags.set([]);
    filters.attachments.set([]);
    filters.moods.set([]);
    filters.status.set([]);
    filters.role.set([]);
    filters.feature.set([]);
    filters.dateFrom.set(null);
    filters.dateTo.set(null);
    // Collapse the filter panel after clearing
    this.isFilter.set(false);
  }

  /** Calculate grid column count for responsive grid (69-99px blocks, 4px gap) */
  calculateCrossAxisCount(containerWidth: number): number {
    const spacing = 4;
    for (let count = 7; count >= 4; count--) {
      const blockSize = (containerWidth - (count - 1) * spacing) / count;
      if (blockSize >= 69 && blockSize <= 99) {
        return count;
      }
    }
    const blockWith4 = (containerWidth - 3 * spacing) / 4;
    if (blockWith4 >= 69) return 4;
    return 3;
  }

  /** Calculate block size for a given container width and column count */
  calculateBlockSize(containerWidth: number, columns: number): number {
    const spacing = 4;
    return (containerWidth - (columns - 1) * spacing) / columns;
  }

  analyticsInit() {
    this.as.analytics$.pipe(takeUntil(this.unSubscribe)).subscribe((init: boolean) => {
      if (init) {
        const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
        this.as.screenViewEvent('drawerAttachment', previousScreen);
        this.storageService.setCurrentScreen('drawerAttachment');
      }
    });
  }

  ngOnDestroy() {
    this.as.screenExitEvent('drawerAttachment', this.screenEnterTime());
    // Signal first to trigger takeUntil, then complete
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
